"use client";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface DisconnectAgencyClientDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
  agencyName: string;
  clientName?: string;
  isDisconnecting?: boolean;
}

/**
 * Confirmation before an agency is disconnected from one of its clients.
 *
 * THIS IS NOT THE CONNECT DIALOG AND MUST NOT LOOK LIKE IT. Removing a
 * connection destroys work — that is the accepted consequence recorded on the
 * story, taken knowing the source document originally said the opposite — and
 * this dialog is the only control standing in front of it.
 *
 * Four things in the copy are requirements rather than taste:
 *
 *   1. It NAMES THE CLIENT, so the wrong row is visibly the wrong row.
 *   2. It says the agency's content trips for that client are deleted, along
 *      with the deliverables, photos and comments inside them.
 *   3. It says photos already delivered into the venue's own Library ARE KEPT.
 *      Without that line an operator reasonably assumes everything goes.
 *   4. The action uses the DESTRUCTIVE variant.
 *
 * Cancelling does nothing at all — no request is made until the action button
 * is pressed.
 */
export function DisconnectAgencyClientDialog({
  open,
  onOpenChange,
  onConfirm,
  agencyName,
  clientName,
  isDisconnecting = false,
}: DisconnectAgencyClientDialogProps) {
  // The dialog stays mounted between opens, so it renders once with no client
  // selected; the name must never come out as an empty pair of quotes.
  const name = clientName?.trim();

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          {/* `break-words`: a venue name can be long and unbroken, and the
              card is a fixed 32rem. */}
          <AlertDialogTitle className="break-words">
            {name
              ? `Remove "${name}" as a client of "${agencyName}"?`
              : "Remove this client?"}
          </AlertDialogTitle>
          <AlertDialogDescription>
            This deletes every content trip {agencyName} ran for{" "}
            {name ?? "this client"}, along with the deliverables, photos and
            comments inside them. Photos already delivered into the venue&apos;s
            Library are kept — those belong to the venue. Nothing else is
            recoverable afterwards.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isDisconnecting}>
            Cancel
          </AlertDialogCancel>
          {/* `preventDefault`: Radix closes the dialog on the action's click
              by default, which would tear it down before the request is even
              accepted. The caller closes it once the server has taken the job
              — or, if the request was refused outright, once it can say so. */}
          <AlertDialogAction
            onClick={(event) => {
              event.preventDefault();
              onConfirm();
            }}
            disabled={isDisconnecting}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
          >
            {isDisconnecting ? "Removing..." : "Yes, remove client"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
